// Плавающая панель управления (README §4.3, §6, UI-кит §10.4 п.3): островок
// внизу по центру полотна, bottom 50, radius 12, --panel, --shadow,
// max-width 100% − 28px. Перетаскивается за «⠿», не выходит за края родителя
// (родитель обязан быть position: relative / absolute).

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import './ui.css'

export interface FloatingPanelProps {
  children: ReactNode
  /** отступ от нижнего края родителя, px (по умолчанию 50) */
  bottom?: number
  /** показывать ручку «⠿» (по умолчанию да) */
  draggable?: boolean
  /** не забирать фокус у редактора при клике (Заметки: pointerdown → preventDefault) */
  keepFocus?: boolean
  className?: string
  style?: CSSProperties
  /** подпись ручки */
  handleTitle?: string
  /** над строкой панели: всплывающие списки режима, элементов и т.п. */
  popups?: ReactNode
}

const EDGE = 14

export function FloatingPanel({
  children, bottom = 50, draggable = true, keepFocus, className, style, handleTitle = 'перенести панель', popups,
}: FloatingPanelProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [shift, setShift] = useState({ x: 0, y: 0 })
  const drag = useRef<{ sx: number; sy: number; x0: number; y0: number } | null>(null)

  /** Зажать сдвиг так, чтобы панель осталась внутри родителя с полем 14 px. */
  const clamp = useCallback((x: number, y: number) => {
    const el = ref.current
    const parent = el?.offsetParent as HTMLElement | null
    if (!el || !parent) return { x, y }
    const pw = parent.clientWidth
    const ph = parent.clientHeight
    const w = el.offsetWidth
    const h = el.offsetHeight
    const maxX = Math.max(0, (pw - w) / 2 - EDGE)
    const minY = -(ph - bottom - h - EDGE)
    const maxY = bottom - EDGE
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(Math.min(0, minY), y)),
    }
  }, [bottom])

  // Окно сузилось — панель не должна остаться за краем.
  useEffect(() => {
    const el = ref.current
    const parent = el?.offsetParent as HTMLElement | null
    if (!el || !parent) return
    const ro = new ResizeObserver(() => setShift((s) => clamp(s.x, s.y)))
    ro.observe(parent)
    return () => ro.disconnect()
  }, [clamp])

  const onDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { sx: event.clientX, sy: event.clientY, x0: shift.x, y0: shift.y }
    document.body.classList.add('abx-dragging')
  }
  const onMove = (event: React.PointerEvent<HTMLSpanElement>) => {
    const d = drag.current
    if (!d) return
    setShift(clamp(d.x0 + event.clientX - d.sx, d.y0 + event.clientY - d.sy))
  }
  const onUp = (event: React.PointerEvent<HTMLSpanElement>) => {
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    document.body.classList.remove('abx-dragging')
  }

  return (
    <div
      ref={ref}
      className={`abx-float${className ? ' ' + className : ''}`}
      style={{
        bottom,
        transform: `translate(calc(-50% + ${shift.x}px), ${shift.y}px)`,
        ...style,
      }}
      onPointerDown={keepFocus ? (event) => {
        // не отдавать фокус кнопкам панели — курсор остаётся в редакторе
        if ((event.target as HTMLElement).closest('input, textarea, [contenteditable="true"]')) return
        event.preventDefault()
      } : undefined}
    >
      {draggable ? (
        <span
          className="abx-float__grip"
          title={handleTitle}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onDoubleClick={() => setShift({ x: 0, y: 0 })}
        >
          ⠿
        </span>
      ) : null}
      {children}
      {popups}
    </div>
  )
}

/** Кнопка островка: рамка --line, radius 8. tone="run" — «▶ пуск» (--okSoft/--okLine/--okText). */
export function FloatButton({
  children, onClick, title, active, tone, disabled,
}: {
  children: ReactNode
  /** событие нужно, чтобы привязать Menu к кнопке: anchor={event.currentTarget} */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  title?: string
  active?: boolean
  tone?: 'plain' | 'filled' | 'run'
  disabled?: boolean
}) {
  return (
    <button
      className={`abx-float__btn tone-${tone ?? 'plain'}${active ? ' is-active' : ''}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

/** Вертикальная черта-разделитель внутри островка («B I | + блок»). */
export function FloatDivider() {
  return <span className="abx-float__div" aria-hidden="true" />
}
